import { useEffect, useState } from 'react';
import type { Membresia, Operaciones } from '../../api/operaciones';
import type { Recurso, Zona } from '../../api/ops/pagos';
import type { Reserva } from '../../api/ops/reservas';
import { rolesDe } from '../../logica/acceso';
import { mayuscula } from './textos';

/**
 * Lo que las pantallas de Reservas leen alrededor de la lista: la zona horaria del negocio (G375), su palabra para lo
 * que se reserva (`vocabulary.resource` de getBusiness o, si el negocio no la cambió, la de su sector; G214), las zonas y
 * los recursos (para «Terraza · Reservado T4») y si tiene el bloque Plano y sala (G796, «Imprimir»). Si algo falla, lo
 * neutro: la zona del navegador, «recurso» y sin nombres.
 */
export interface ContextoReservas {
    zona: string;
    palabra: string;
    zonas: readonly Zona[];
    recursos: readonly Recurso[];
    planoSala: boolean;
    /** Bloques contratados (`product_code` de listEntitlements); vacío mientras carga o si falla. */
    bloques: readonly string[];
    nombreNegocio: string;
    /** Los roles de la persona («Encargado, Socio»), para el texto de «sin permiso» de 8.8 y 8.10 (G949, ADR-0240). */
    rol: string;
}

const ZONA_NAVEGADOR = Intl.DateTimeFormat().resolvedOptions().timeZone;

export async function todas<T>(pedir: (cursor: string | null) => Promise<{ data: T[]; next_cursor: string | null }>): Promise<T[]> {
    const lista: T[] = [];
    let cursor: string | null = null;
    for (let i = 0; i < 20; i += 1) {
        const pagina = await pedir(cursor);
        lista.push(...pagina.data);
        cursor = pagina.next_cursor;
        if (cursor === null) {
            break;
        }
    }
    return lista;
}

export function useContextoReservas(ops: Operaciones, negocio: string, membresia: Membresia | undefined, nombreNegocio: string): ContextoReservas {
    const zonaMembresia = membresia?.timezone;
    const rol = membresia === undefined ? '' : rolesDe(membresia);
    const [c, setC] = useState<ContextoReservas>(() => ({ zona: zonaMembresia ?? ZONA_NAVEGADOR, palabra: 'recurso', zonas: [], recursos: [], planoSala: false, bloques: [], nombreNegocio, rol }));
    useEffect(() => {
        let vivo = true;
        void Promise.all([
            ops.getBusinessConVersion(negocio).then((n) => n.datos).catch(() => null),
            ops.listEntitlements(negocio).catch(() => null),
            todas((cursor) => ops.listZones(negocio, { cursor, limit: 100 })).catch(() => [] as Zona[]),
            todas((cursor) => ops.listResources(negocio, { cursor, limit: 100 })).catch(() => [] as Recurso[]),
        ]).then(async ([n, bloques, zonas, recursos]) => {
            let palabra = n?.vocabulary?.resource?.trim() ?? '';
            if (palabra === '' && n !== null) {
                const sectores = await ops.listBusinessSectors('es-ES').catch(() => null);
                palabra = sectores?.data.find((s) => s.code === n.sector)?.vocabulary.resource?.trim() ?? '';
            }
            if (!vivo) {
                return;
            }
            setC({
                zona: zonaMembresia ?? n?.timezone ?? ZONA_NAVEGADOR,
                palabra: palabra === '' ? 'recurso' : palabra.toLocaleLowerCase('es'),
                zonas,
                recursos,
                planoSala: (bloques?.entitlements ?? []).some((e) => e.product_code === 'plano_sala'),
                bloques: (bloques?.entitlements ?? []).map((e) => e.product_code),
                nombreNegocio: n?.name ?? nombreNegocio,
                rol,
            });
        });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, zonaMembresia, nombreNegocio, rol]);
    return c;
}

/** Nombre de la zona de una reserva. */
export function nombreZona(c: ContextoReservas, zonaId: string): string {
    return c.zonas.find((z) => z.id === zonaId)?.name ?? '';
}

/** «Reservado S2» o «Reservado S2 + Reservado S3» (una unión); vacío si no tiene recurso. */
export function nombreRecursos(c: ContextoReservas, r: Pick<Reserva, 'resources'>): string {
    return r.resources
        .map((a) => {
            const recurso = c.recursos.find((x) => x.id === a.resource_id);
            return recurso === undefined ? '' : `${mayuscula(c.palabra)} ${recurso.code ?? ''}`;
        })
        .filter((x) => x !== '')
        .join(' + ');
}

/** «Terraza · Reservado T4» (o solo la zona). */
export function dondeEs(c: ContextoReservas, r: Pick<Reserva, 'zone_id' | 'resources'>): string {
    return [nombreZona(c, r.zone_id), nombreRecursos(c, r)].filter((x) => x !== '').join(' · ');
}

/** El turno de la zona en que cae la reserva (`shift_id`), o null. */
export function nombreTurno(c: ContextoReservas, r: Pick<Reserva, 'zone_id' | 'shift_id'>): string | null {
    if (r.shift_id == null) {
        return null;
    }
    return c.zonas.find((z) => z.id === r.zone_id)?.shifts?.find((t) => t.id === r.shift_id)?.name ?? null;
}

/**
 * Nombres de los clientes de una lista en una sola llamada (`listCustomers` con `ids`, G303). Sin permiso para ver
 * clientes, ninguno (la fila dice «Cliente»).
 */
export function useNombres(ops: Operaciones, negocio: string, ids: readonly string[], puede: boolean): Readonly<Record<string, string>> {
    const [nombres, setNombres] = useState<Readonly<Record<string, string>>>({});
    const faltan = puede ? [...new Set(ids)].filter((x) => !(x in nombres)).sort().join(',') : '';
    useEffect(() => {
        if (faltan === '') {
            return;
        }
        let vivo = true;
        const lista = faltan.split(',');
        ops.listCustomersByIds(negocio, lista)
            .then((l) => {
                if (vivo) {
                    setNombres((x) => ({ ...x, ...Object.fromEntries(lista.map((id) => [id, l.data.find((cl) => cl.id === id)?.display_name ?? ''])) }));
                }
            })
            .catch(() => {
                if (vivo) {
                    setNombres((x) => ({ ...x, ...Object.fromEntries(lista.map((id) => [id, ''])) }));
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, faltan]);
    return nombres;
}

/** El nombre que se pinta: el del cliente, «Sin reserva» en un walk-in sin datos o «Cliente» si no se puede leer. */
export function nombreDe(r: Pick<Reserva, 'customer_id'>, nombres: Readonly<Record<string, string>>): string {
    if (r.customer_id == null) {
        return 'Sin reserva';
    }
    const n = nombres[r.customer_id];
    return n === undefined || n === '' ? 'Cliente' : n;
}
